import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, X, PenLine, Download, FileText, ShieldAlert, Sparkles, SkipForward } from 'lucide-react';
import { SIG_FONTS, HW_STYLES } from './DisputeLetterModal';
import { getLetterStyle, saveLetterStyle } from '../lib/letterStyle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/dispute-letters/batch`;
const MODES = [
  ['suggested', FileText, 'All suggested letters (2 per account)', '§605B identity theft + the fastest other route — you pick'],
  ['identity_theft', ShieldAlert, 'Identity theft on every account', '§605B 4-business-day block + deletion demand'],
  ['ai_best', Sparkles, 'AI picks the fastest route', '§605B when it fits, else FCRA / FDCPA / Metro 2'],
];

const Picker = ({ title, items, value, onChange, previewText, testPrefix }) => (
  <div>
    <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5">{title}</p>
    <div className="flex gap-2 overflow-x-auto pb-1" data-testid={`${testPrefix}-grid`}>
      {items.map((f) => (
        <button key={f.value} type="button" onClick={() => onChange(f.value)} data-testid={`${testPrefix}-${f.value}`}
          className={`shrink-0 min-w-[120px] rounded-lg border px-3 py-2 text-left transition-colors ${value === f.value ? 'border-amber-400 bg-amber-500/15' : 'border-zinc-800 bg-zinc-900 hover:border-zinc-600'}`}>
          <span className="block text-lg leading-tight text-white whitespace-nowrap" style={{ fontFamily: f.preview }}>{previewText || f.label}</span>
          <span className="block text-[10px] text-zinc-500">{f.subLabel}</span>
        </button>
      ))}
    </div>
  </div>
);

const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 3000);
};

// After extraction: one click writes a unique AI letter per account, in one handwriting + signature, each downloaded separately.
export default function BatchLettersModal({ open, accounts, onClose, clientName, clientAddressLines }) {
  const [style, setStyle] = useState(getLetterStyle);
  const [mode, setMode] = useState('suggested');
  const [length, setLength] = useState('short');
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearInterval(timer.current), []);
  useEffect(() => { if (open) { setJob(null); setStyle(getLetterStyle()); } }, [open]);
  if (!open) return null;

  const update = (patch) => { const next = { ...style, ...patch }; setStyle(next); saveLetterStyle(next); };
  // Each letter downloads as its own PDF, named "<Account> - <Dispute type>.pdf".
  const downloadOne = async (jobId, name) => {
    const r = await axios.get(`${API}/${jobId}/file/${encodeURIComponent(name)}`, { responseType: 'blob', timeout: 60000 });
    saveBlob(r.data, name);
  };
  const finish = async (j) => {
    clearInterval(timer.current);
    if (!j.files?.length) { toast.error('No letters could be generated — try again'); return; }
    let ok = 0;
    for (const f of j.files) {
      try { await downloadOne(j.job_id, f.name); ok += 1; } catch (_e) { /* listed below for manual download */ }
      await new Promise((r) => setTimeout(r, 700));
    }
    toast.success(`${ok} of ${j.files.length} letter${j.files.length > 1 ? 's' : ''} downloaded${j.errors?.length ? ` · ${j.errors.length} failed` : ''}. Tap any letter below to download it again`);
  };
  const start = async () => {
    if (!clientName?.trim()) { toast.error('Enter the client name in Client Info first'); return; }
    const payload = accounts.map((a) => ({
      id: String(a.id), name: String(a.displayName || a.creditorName || a.name || 'Account').split(' - ')[0].trim() || 'Account',
      amount: a.fraudAmount || a.amount || null, date: a.dateOpened || null, category: a.category || null,
    }));
    try {
      const r = await axios.post(API, { client_name: clientName, client_address_lines: clientAddressLines, accounts: payload,
        handwriting_style: style.hwStyle, signature_font: style.signatureFont, mode, letter_length: length });
      setJob({ ...r.data, status: 'running', done: 0, files: [], errors: [] });
      let last = Date.now(); let lastDone = 0;
      timer.current = setInterval(async () => {
        try {
          const s = (await axios.get(`${API}/${r.data.job_id}`)).data;
          if (s.done !== lastDone) { lastDone = s.done; last = Date.now(); }
          setJob(s);
          if (s.status === 'done') finish(s);
          else if (Date.now() - last > 240000) { clearInterval(timer.current); toast.error('Letter generation stalled — please try again'); setJob(null); }
        } catch (_e) { /* keep polling */ }
      }, 3000);
    } catch (e) { toast.error(`Could not start: ${e.response?.data?.detail || e.message}`); }
  };
  const running = job && job.status !== 'done';
  const pct = job ? Math.round((job.done / Math.max(job.total, 1)) * 100) : 0;
  const signName = (clientName || 'Your Name').trim().toLowerCase().replace(/(^|[\s'-])\S/g, (m) => m.toUpperCase());

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-3" data-testid="batch-letters-modal">
      <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl border border-zinc-700 bg-zinc-950 p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-white">Generate All Dispute Letters</h2>
            <p className="text-xs text-zinc-400">Unique, aggressive deletion letters for every account of {clientName || 'this client'} ({accounts?.length || 0} account{accounts?.length === 1 ? '' : 's'}), addressed to all 3 bureaus. Each letter downloads on its own, named by account and dispute type.</p>
          </div>
          {!running && <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="batch-letters-close"><X className="w-5 h-5" /></button>}
        </div>

        {!job && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {MODES.map(([v, Icon, label, sub]) => (
                <button key={v} type="button" onClick={() => setMode(v)} data-testid={`batch-mode-${v}`}
                  className={`flex items-start gap-2 rounded-lg border p-2.5 text-left transition-colors ${mode === v ? 'border-emerald-400 bg-emerald-500/10' : 'border-zinc-800 hover:border-zinc-600'}`}>
                  <Icon className={`w-4 h-4 mt-0.5 ${mode === v ? 'text-emerald-300' : 'text-zinc-500'}`} />
                  <span><span className="block text-sm font-semibold text-white">{label}</span><span className="block text-[11px] text-zinc-400">{sub}</span></span>
                </button>
              ))}
            </div>
            <div className="space-y-1" data-testid="batch-length">
              <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Letter length</p>
              <div className="flex gap-2">
                {[['short', 'Short · 2-3 pages', 'Aggressive and to the point. Every removal tool kept (default)'], ['full', 'Long · 5-8 pages', 'Same tools, more explanation and legal detail']].map(([v, l, d]) => (
                  <button key={v} type="button" onClick={() => setLength(v)} data-testid={`batch-length-${v}`}
                    className={`flex-1 rounded-lg border p-2 text-left transition-colors ${length === v ? 'border-cyan-400 bg-cyan-500/10' : 'border-zinc-800 hover:border-zinc-600'}`}>
                    <span className="block text-sm font-semibold text-white">{l}</span><span className="block text-[11px] text-zinc-400">{d}</span>
                  </button>
                ))}
              </div>
            </div>
            <Picker title="Handwriting font (used for every letter)" items={HW_STYLES} value={style.hwStyle} onChange={(v) => update({ hwStyle: v })} testPrefix="batch-hw" />
            <Picker title="Signature" items={SIG_FONTS} value={style.signatureFont} onChange={(v) => update({ signatureFont: v })} previewText={signName} testPrefix="batch-sig" />
            <p className="text-[11px] text-zinc-500"><PenLine className="inline w-3 h-3 mr-1 -mt-0.5" />These become the default font and signature in the dispute letter tool too. You can change them there anytime.</p>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 pt-1">
              <button type="button" onClick={onClose} data-testid="batch-letters-skip"
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-zinc-700 px-4 py-2.5 text-sm text-zinc-300 hover:text-white">
                <SkipForward className="w-4 h-4" />Skip — continue to accounts
              </button>
              <button type="button" onClick={start} data-testid="batch-letters-generate"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 px-5 py-2.5 text-sm font-bold text-black hover:bg-amber-400">
                <Download className="w-4 h-4" />Generate all letters ({(accounts?.length || 0) * (mode === 'suggested' ? 2 : 1)}{mode === 'ai_best' ? '+' : ''})
              </button>
            </div>
          </>
        )}

        {job && (
          <div className="space-y-3" data-testid="batch-letters-progress">
            <div className="flex items-center gap-2 text-sm text-zinc-200">
              {running && <Loader2 className="w-4 h-4 animate-spin text-amber-400" />}
              Writing letters… {job.done} of {job.total} accounts done
            </div>
            <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
              <div className="h-full bg-amber-500 transition-[width] duration-500" style={{ width: `${pct}%` }} data-testid="batch-letters-bar" />
            </div>
            <ul className="space-y-1 max-h-60 overflow-y-auto" data-testid="batch-letters-files">
              {job.files.map((f) => (
                <li key={f.name}>
                  <button type="button" disabled={running} onClick={() => downloadOne(job.job_id, f.name).catch((e) => toast.error(`Download failed: ${e.message}`))}
                    data-testid={`batch-letter-file-${f.name}`}
                    className="flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left text-xs text-zinc-300 hover:bg-zinc-800 hover:text-white disabled:hover:bg-transparent">
                    <FileText className="w-3.5 h-3.5 text-emerald-400 shrink-0" /><span className="truncate">{f.name}</span>
                    {!running && <Download className="ml-auto w-3.5 h-3.5 text-amber-400 shrink-0" />}
                  </button>
                </li>
              ))}
              {job.errors.map((e) => <li key={e.account} className="text-xs text-red-400">{e.account}: failed ({e.error})</li>)}
            </ul>
            <p className="text-[11px] text-zinc-500">{running ? 'Each letter downloads automatically when they are all ready.' : 'Done. Tap any letter to download it again.'}</p>
            {!running && (
              <button type="button" onClick={onClose} data-testid="batch-letters-done"
                className="w-full rounded-lg bg-amber-500 py-2.5 text-sm font-bold text-black hover:bg-amber-400">Continue with FTC flow</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
